export type DiffPart = { kind: 'same' | 'added' | 'removed'; text: string }

const diff = (from: string[], to: string[]): DiffPart[] => {
  const lcs = Array.from({ length: from.length + 1 }, () =>
    new Array<number>(to.length + 1).fill(0),
  )
  const common = (i: number, j: number) => lcs[i]?.[j] ?? 0
  for (let i = from.length - 1; i >= 0; i--) {
    const row = lcs[i] ?? []
    for (let j = to.length - 1; j >= 0; j--) {
      row[j] =
        from[i] === to[j]
          ? common(i + 1, j + 1) + 1
          : Math.max(common(i + 1, j), common(i, j + 1))
    }
  }
  const parts: DiffPart[] = []
  let i = 0
  let j = 0
  while (i < from.length || j < to.length) {
    const removed = from[i]
    const added = to[j]
    if (removed !== undefined && removed === added) {
      parts.push({ kind: 'same', text: removed })
      i++
      j++
    } else if (
      added !== undefined &&
      (removed === undefined || common(i, j + 1) > common(i + 1, j))
    ) {
      parts.push({ kind: 'added', text: added })
      j++
    } else if (removed !== undefined) {
      parts.push({ kind: 'removed', text: removed })
      i++
    }
  }
  return parts
}

const words = (line: string) => line.split(/(\s+)/)

/**
 * Lines that differ between two versions of lyrics, one row per changed line.
 * A changed line is diffed word by word; a line only one side has is a single part.
 */
export const changedLyricLines = (
  current: string,
  ai: string,
): DiffPart[][] => {
  const rows: DiffPart[][] = []
  let removed: string[] = []
  let added: string[] = []
  const flush = () => {
    const paired = Math.min(removed.length, added.length)
    removed.slice(0, paired).forEach((line, k) => {
      rows.push(diff(words(line), words(added[k] ?? '')))
    })
    removed
      .slice(paired)
      .forEach(text => rows.push([{ kind: 'removed', text }]))
    added.slice(paired).forEach(text => rows.push([{ kind: 'added', text }]))
    removed = []
    added = []
  }
  diff(current.split('\n'), ai.split('\n')).forEach(part => {
    if (part.kind === 'same') flush()
    else if (part.kind === 'removed') removed.push(part.text)
    else added.push(part.text)
  })
  flush()
  return rows
}
